import React, { useState, useEffect, useContext, Fragment, useMemo } from 'react'
import { useForm, useWatch, useFieldArray } from 'react-hook-form'
import * as yup from "yup"
import { yupResolver } from "@hookform/resolvers/yup";

import Button from '@/components/Button'
import { ModalContext } from '@/contexts/ModalContext'
// import { CheckCircleIcon, CheckIcon } from '@heroicons/react/solid'
import { getFunctions, httpsCallable } from "firebase/functions";
import { StakeContext } from '@/contexts/StakeContext'
import Loader from '@/components/Loader'
import { AuthContext } from '@/contexts/AuthContext'
import { PlusIcon } from '@heroicons/react/outline'
import modalRoutes from '@/modalRoutes'
import { convertAmount } from '@/utils/currencyResolver'
import { classNames } from '@/utils/classNames'
import resolveErrors from '@/utils/resolveErrors';
import { RadioGroup } from '@headlessui/react'
import errorToast from '../../utils/errorToast';
import { useBalance } from '@/hooks/useBalance'

function CheckIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <circle cx={12} cy={12} r={12} fill="#fff" opacity="0.2" />
      <path
        d="M7 13l3 3 7-7"
        stroke="#fff"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export default function CreateDuel() {
  const functions = getFunctions();
  const createDuel = httpsCallable(functions, 'createDuel');
  const { userAccounts, userBalance, userPrimaryAccount } = useContext(AuthContext);
  const { isLoading, taxRate } = useContext(StakeContext)
  const { closeModal } = useContext(ModalContext)
  const [serverErrors, setServerErrors] = useState(null)
  const [plan, setPlan] = useState('TentWhip')
  const [mockValue, setMockValue] = useState(0)

  const schema = yup.object().shape({
    amount: yup.number().required().positive().min(0).max(userBalance),
    allowed_weapons: yup.array().of(yup.string()).min(1).required(),
  });

  const { register, trigger, handleSubmit, control, setValue, getValues, formState: { errors, isSubmitting }, reset } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      amount: 0,
      allowed_weapons: plan == "TentWhip" ? ["12006"] : plan == "TentDDS" ? ["12006", "5698"] : ["0"],
    }
  });

  const amount = useWatch({
    control,
    name: "amount",
    defaultValue: 0
  });

  const { displayValue, value } = useBalance(amount)

  const onSubmitHandler = async (data) => {
    try {
      await createDuel({
        allowedWeapons: plan == "TentWhip" ? ["12006"] : plan == "TentDDS" ? ["12006", "5698"] : ["0"],
        wager: Number(data.amount)
      })
      closeModal()
    } catch (err) {
      errorToast(err)
    }
  }

  const handleChange = e => {
    setValue('amount', e.target.value * 1000000)
  }

  return (
    !isLoading ? (
      <div>
        <form onSubmit={handleSubmit(onSubmitHandler)} className="grid gap-4 mt-4">
          <div>
            <div className="mb-1 flex justify-between items-baseline">
              <label className="block font-medium">Wager Amount <span className="text-xs">({convertAmount(amount)})</span></label>
              <div className="flex items-baseline">
                <span className="text-xs">Available: {convertAmount(userBalance)}</span>
                <button onClick={() => setValue('amount', userBalance)} type="button" className="text-xs ml-1 underline text-blue-600 hover:text-blue-700">Use Max</button>
              </div>
            </div>
            <div className="relative">
              <input {...register('amount')} type="hidden" required className={errors?.amount ? '!border-red-500 absolute' : ' absolute'} />
              <input type="number" step=".01" value={displayValue} onChange={handleChange} required className={errors?.amount ? '!border-red-500' : 'border-transparent'} />
              <div className="absolute right-0 inset-y-0 flex p-1">
                <div className="px-3 bg-gray-800 rounded shadow font-medium text-sm flex items-center justify-center">M</div>
              </div>
            </div>
          </div>
          <div>
            <label className="block mb-1 font-medium">Payout</label>
            <input value={convertAmount((amount * 2) * (1 - taxRate?.value))} type="text" className="bg-gray-900 border-transparent shadow-md" readOnly />
          </div>
          <div>
            <label className="block mb-1 font-medium">Your Account</label>
            {userAccounts?.length > 0 ? (
              <input value={userPrimaryAccount?.username} type="text" className="bg-gray-900 border-transparent shadow-md" readOnly />
            ) : (
              <div className={classNames('flex flex-col items-center p-3 border-dotted border rounded', errors?.account && 'border-2 !border-red-500')}>
                <p>Please link an account before creating a duel.</p>
                <Button link to={`${modalRoutes.LINK_ACCOUNT.search}&referrer=createDuel`} className="p-2 flex items-center justify-center leading-none mt-2" modifier="secondary">
                  <PlusIcon className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
          <div>
            <label className="block mb-1 font-medium">Allowed Weapons</label>
            <div className="">
              <RadioGroup className="grid grid-cols-3 gap-2" value={plan} onChange={setPlan}>
                <RadioGroup.Option value={"TentWhip"}>
                  {({ checked }) => (
                    <span className={classNames('text-sm cursor-pointer bg-gray-900 border-2 relative rounded py-2.5 transition duration-300 px-4 flex items-center justify-center font-medium text-white', checked ? 'border-purple-600 opacity-100' : 'border-transparent opacity-60')}>Whip</span>
                  )}
                </RadioGroup.Option>
                <RadioGroup.Option value="TentDDS">
                  {({ checked }) => (
                    <span className={classNames('text-sm cursor-pointer bg-gray-900 border-2 relative rounded py-2.5 transition duration-300 px-4 flex items-center justify-center font-medium text-white', checked ? 'border-purple-600 opacity-100' : 'border-transparent opacity-60')}>Whip + DDS</span>
                  )}
                </RadioGroup.Option>
                <RadioGroup.Option value="Any">
                  {({ checked }) => (
                    <span className={classNames('text-sm cursor-pointer bg-gray-900 border-2 relative rounded py-2.5 transition duration-300 px-4 flex items-center justify-center font-medium text-white', checked ? 'border-purple-600 opacity-100' : 'border-transparent opacity-60')}>Any</span>
                  )}
                </RadioGroup.Option>
              </RadioGroup>
            </div>
            {errors?.allowed_weapons && <p className="bg-red-200 p-2 border border-red-800 text-center text-red-800 rounded-md mt-2 text-xs">{errors.allowed_weapons?.message}</p>}
          </div>
          {serverErrors && <p className="text-xs text-red-500 font-medium">{serverErrors}</p>}
          <div className="grid grid-cols gap-2">
            <Button type="submit" modifier="secondary" className="py-2.5">
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  Creating
                  <svg className="animate-spin ml-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </span>
              ) : (
                <span>Start</span>
              )}
            </Button>
            <Button type="button" onClick={closeModal} modifier="gray" className="py-2.5">Cancel</Button>
          </div>
        </form>
      </div>
    ) : (
      <div className="py-12">
        <Loader />
      </div>
    )
  )
}